/**
 * Property selection context hook (Phase 1 redesign).
 *
 * This is a THIN, non-invasive layer over the existing `selectedProperty`
 * state that already lives in App.js. It provides:
 *
 *   • localStorage persistence  → user stays on the same property between
 *                                 page reloads (previously it always reset
 *                                 to the first property in the list).
 *   • allPropertiesMode         → when true, the app is in the master
 *                                 portfolio view; property-scoped modules
 *                                 should treat this as "no scope".
 *
 * The hook is intentionally lightweight — it does not fetch anything and
 * does not touch any MMT-related code path.
 */
import { useCallback, useEffect, useState } from 'react';

const LS_PROPERTY_KEY = 'phase1_selected_property_id';
const LS_ALL_MODE_KEY = 'phase1_all_properties_mode';

/**
 * @param {object|null} externalSelected  the App.js-owned `selectedProperty` object
 * @param {(p: object|null) => void} externalSetter  the App.js-owned `setSelectedProperty`
 * @param {Array<object>} allProperties  the loaded property list (from `fetchProperties`)
 */
export function usePropertyContext(externalSelected, externalSetter, allProperties) {
  const [allPropertiesMode, setAllPropertiesModeState] = useState(() => {
    try { return localStorage.getItem(LS_ALL_MODE_KEY) === '1'; } catch (_) { return false; }
  });

  // Hydrate the initial selection from localStorage once properties have loaded.
  useEffect(() => {
    if (!Array.isArray(allProperties) || allProperties.length === 0) return;
    try {
      const savedId = localStorage.getItem(LS_PROPERTY_KEY);
      if (savedId && (!externalSelected || externalSelected.id !== savedId)) {
        const match = allProperties.find((p) => p && p.id === savedId);
        if (match) externalSetter(match);
      }
    } catch (_) { /* localStorage disabled — non-fatal */ }
  }, [allProperties]);

  // Persist the App.js-owned selection whenever it changes.
  useEffect(() => {
    if (!externalSelected || !externalSelected.id) return;
    try { localStorage.setItem(LS_PROPERTY_KEY, externalSelected.id); } catch (_) { /* noop */ }
  }, [externalSelected]);

  const setAllPropertiesMode = useCallback((mode) => {
    const b = Boolean(mode);
    setAllPropertiesModeState(b);
    try { localStorage.setItem(LS_ALL_MODE_KEY, b ? '1' : '0'); } catch (_) { /* noop */ }
  }, []);

  return { allPropertiesMode, setAllPropertiesMode };
}
